import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useI18n } from "@/context/PrefsContext";
import { StatusBadge, Badge } from "@/components/ui-kit";
import { ArrowLeft, Phone, Mail, MapPin, KeyRound, Car, FileText, Calendar, Receipt } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/clients/${id}`).then(({ data }) => setData(data)).catch(() => navigate("/clients"));
  }, [id]);

  if (!data) return null;
  const { client, jobs, invoices, estimates } = data;

  return (
    <div data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-2 text-[#5A189A] text-sm font-medium mb-4 hover:text-[#D4AF37]">
        <ArrowLeft size={16} /> {t("Back to Customers")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile */}
        <div className="mt-card p-6 lg:col-span-1 h-fit">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5A189A] to-[#7B2CBF] text-white flex items-center justify-center text-2xl font-display font-bold">
              {client.name?.[0]}
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-[#121212]">{client.name}</h1>
              <Badge>{client.cleaning_frequency}</Badge>
            </div>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <Info icon={Phone} value={client.phone} />
            <Info icon={Mail} value={client.email} />
            <Info icon={MapPin} value={[client.address, client.city, client.state, client.zip].filter(Boolean).join(", ")} />
            <Info icon={KeyRound} label={t("Gate Code")} value={client.gate_code} />
            <Info icon={Car} label={t("Parking")} value={client.parking_instructions} />
          </div>
          {client.property_notes && <NoteBlock title={t("Property Notes")} text={client.property_notes} />}
          {client.customer_notes && <NoteBlock title={t("Customer Notes")} text={client.customer_notes} />}
        </div>

        {/* History */}
        <div className="lg:col-span-2 space-y-6">
          <HistoryCard icon={Calendar} title={t("Cleaning History")} empty={t("No jobs yet")}>
            {jobs.map((j) => (
              <Row key={j.id} left={j.service} sub={`${j.date} · ${j.job_number}`} right={money(j.amount)} badge={j.status} />
            ))}
          </HistoryCard>
          <HistoryCard icon={FileText} title={t("Estimates")} empty={t("No estimates yet")}>
            {estimates.map((e) => (
              <Row key={e.id} left={e.estimate_number} sub={e.created_at?.slice(0, 10)} right={money(e.total)} badge={e.status} />
            ))}
          </HistoryCard>
          <HistoryCard icon={Receipt} title={t("Invoices")} empty={t("No invoices yet")}>
            {invoices.map((inv) => (
              <Row key={inv.id} left={inv.invoice_number} sub={`Balance ${money(inv.balance_due)}`} right={money(inv.total)} badge={inv.status} />
            ))}
          </HistoryCard>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[#5A189A] mt-0.5" />
      <div>
        {label && <div className="text-xs text-[#9b8bb5]">{label}</div>}
        <div className="text-[#121212]">{value}</div>
      </div>
    </div>
  );
}

function NoteBlock({ title, text }) {
  return (
    <div className="mt-4 p-3 rounded-xl bg-[#faf8fd]">
      <div className="text-xs font-semibold text-[#5A189A] uppercase tracking-wider mb-1">{title}</div>
      <div className="text-sm text-[#52525B]">{text}</div>
    </div>
  );
}

function HistoryCard({ icon: Icon, title, empty, children }) {
  const arr = React.Children.toArray(children);
  return (
    <div className="mt-card p-6">
      <h3 className="font-display text-lg font-semibold text-[#121212] mb-3 flex items-center gap-2">
        <Icon size={18} className="text-[#5A189A]" /> {title}
      </h3>
      {arr.length === 0 ? <div className="text-sm text-[#9b8bb5] py-4">{empty}</div> : <div className="divide-y divide-[#f4f0fa]">{children}</div>}
    </div>
  );
}

function Row({ left, sub, right, badge }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="font-medium text-sm text-[#121212]">{left}</div>
        <div className="text-xs text-[#52525B]">{sub}</div>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-medium text-sm">{right}</span>
        <StatusBadge status={badge} />
      </div>
    </div>
  );
}
